
import React, { useState, useMemo } from 'react';
import { LogEntry, Project, WbsItem, LogStatus, ScopeAnalysis, ScopeClassification, AppTheme } from '../types';
import { auditLogWithGemini } from '../services/geminiService';
import NewLogModal from './NewLogModal';
import CoDraftModal from './CoDraftModal';

interface LogsPageProps {
  project: Project;
  wbs: WbsItem[];
  logs: LogEntry[];
  setLogs: React.Dispatch<React.SetStateAction<LogEntry[]>>;
  onBack: () => void;
  theme: AppTheme;
}

const LogsPage: React.FC<LogsPageProps> = ({ project, wbs, logs, setLogs, onBack, theme }) => {
  const [selectedLogId, setSelectedLogId] = useState<string | null>(logs[0]?.id || null);
  const [isNewLogModalOpen, setIsNewLogModalOpen] = useState(false);
  const [isCoModalOpen, setIsCoModalOpen] = useState(false);
  const [auditing, setAuditing] = useState(false);

  const selectedLog = useMemo(() => logs.find(l => l.id === selectedLogId), [logs, selectedLogId]);

  const handleAuditLogs = async () => {
    setAuditing(true);
    try {
      const updatedLogs = await Promise.all(logs.map(async (log) => {
        if (log.status === LogStatus.NEW) {
          const analysis = await auditLogWithGemini(log, project, wbs);
          return {
            ...log,
            status: analysis.recommendedAction === 'DraftCO' ? LogStatus.FLAGGED_FOR_CO : LogStatus.REVIEWED,
            aiAnalysis: analysis
          };
        }
        return log;
      }));
      setLogs(updatedLogs);
    } catch (err) {
      console.error("Audit failed", err);
    } finally {
      setAuditing(false);
    }
  };

  const getLogItemStyle = (isSelected: boolean) => {
    if (isSelected) return theme === AppTheme.BAMBOO ? 'bg-[#faedcd]' : theme === AppTheme.OCEAN ? 'bg-[#e2f3ff]' : 'bg-[#f2f2f2]';
    return 'hover:bg-slate-50/50';
  };

  const getActionBtnStyle = () => {
    switch(theme) {
      case AppTheme.BAMBOO: return "bg-[#283618] text-[#fefae0]";
      case AppTheme.OCEAN: return "bg-[#003049] text-white";
      default: return "bg-black text-white";
    }
  };

  const groupedLogs = useMemo(() => {
    return logs.reduce((acc, log) => {
      const date = log.date;
      if (!acc[date]) acc[date] = [];
      acc[date].push(log);
      return acc;
    }, {} as Record<string, LogEntry[]>);
  }, [logs]);

  return (
    <div className="flex flex-col h-screen">
      {/* Header */}
      <header className="bg-white px-8 md:px-12 py-8 flex justify-between items-center shrink-0 border-b border-black/5 rounded-b-[2.5rem] shadow-sm">
        <div className="flex items-center gap-8">
          <button onClick={onBack} className="opacity-20 hover:opacity-100 transition-all p-2 -ml-2 hover:scale-110">
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
          </button>
          <div>
            <h1 className="text-2xl font-light tracking-tight truncate max-w-md">{project.name}</h1>
            <p className="text-[8px] font-black opacity-30 uppercase tracking-[0.4em] mt-1">Audit Bridge</p>
          </div>
        </div>
        <div className="flex gap-4">
          <button 
            onClick={() => setIsNewLogModalOpen(true)}
            className="px-6 py-3 text-[9px] font-black uppercase tracking-[0.2em] bg-white border border-black/10 rounded-2xl hover:border-black transition-all shadow-sm active:scale-95"
          >
            New Log
          </button>
          <button 
            onClick={handleAuditLogs}
            disabled={auditing}
            className={`${getActionBtnStyle()} px-6 py-3 text-[9px] font-black uppercase tracking-[0.2em] rounded-2xl opacity-100 disabled:opacity-50 flex items-center gap-3 transition-all shadow-lg active:scale-95`}
          >
            {auditing ? (
              <div className="w-3 h-3 border-2 border-white/20 border-t-white rounded-full animate-spin"></div>
            ) : (
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-7.618 3.016 11.955 11.955 0 01-4.616 11.037c1.64 3.107 4.909 5.31 8.614 6.006 3.705-.696 6.974-2.899 8.614-6.006a11.955 11.955 0 01-4.616-11.037z" />
              </svg>
            )}
            Audit Risk
          </button>
        </div>
      </header>

      <main className="flex flex-1 overflow-hidden p-6 gap-6">
        {/* Stream List */}
        <div className="w-80 md:w-96 bg-white rounded-[2rem] border border-black/5 overflow-hidden flex flex-col shadow-sm">
          <div className="p-6 border-b border-black/5">
            <h3 className="text-[8px] font-black uppercase tracking-[0.3em] opacity-40">Audit Stream</h3>
          </div>
          <div className="flex-1 overflow-y-auto">
            {(Object.entries(groupedLogs) as [string, LogEntry[]][]).sort().reverse().map(([date, dayLogs]) => (
              <div key={date}>
                <div className="bg-black/5 px-6 py-3 text-[8px] font-black opacity-20 border-b border-black/5 sticky top-0 uppercase tracking-[0.3em] z-10">
                  {new Date(date).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}
                </div>
                {dayLogs.map((log) => (
                  <div 
                    key={log.id}
                    onClick={() => setSelectedLogId(log.id)}
                    className={`px-6 py-5 border-b border-black/5 cursor-pointer transition-all ${getLogItemStyle(selectedLogId === log.id)}`}
                  >
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-[8px] font-black opacity-40 uppercase tracking-widest">{log.logType}</span>
                      {log.status === LogStatus.FLAGGED_FOR_CO && (
                        <div className="w-1.5 h-1.5 bg-rose-500 rounded-full animate-pulse shadow-sm"></div>
                      )}
                    </div>
                    <p className={`text-xs leading-relaxed font-bold line-clamp-2 ${selectedLogId === log.id ? 'opacity-100' : 'opacity-40'}`}>
                      {log.text}
                    </p>
                  </div>
                ))}
              </div>
            ))}
          </div>
        </div>

        {/* Detail Panel */}
        <div className="flex-1 bg-white rounded-[2rem] border border-black/5 overflow-y-auto shadow-sm">
          {selectedLog ? (
            <div className="max-w-3xl mx-auto p-12 md:p-16 space-y-12 animate-in fade-in duration-700">
              <section className="space-y-6">
                <div className="flex justify-between items-start">
                  <div className="flex items-center gap-5">
                    <div className={`w-12 h-12 rounded-2xl ${theme === AppTheme.BAMBOO ? 'bg-[#faedcd]' : 'bg-black/5'} flex items-center justify-center font-black text-sm border border-black/5 shadow-inner`}>
                      {selectedLog.authorName.split(' ').map(n => n[0]).join('')}
                    </div>
                    <div>
                      <h3 className="text-base font-bold">{selectedLog.authorName}</h3>
                      <p className="text-[9px] opacity-40 font-bold uppercase tracking-widest mt-0.5">{selectedLog.authorRole} • {selectedLog.location}</p>
                    </div>
                  </div>
                  <span className="text-[9px] font-black opacity-20 uppercase tracking-[0.3em]">{selectedLog.date}</span>
                </div>
                
                <p className="text-xl md:text-2xl font-extralight leading-relaxed whitespace-pre-wrap">{selectedLog.text}</p>
                
                <div className="flex flex-wrap gap-2 pt-2">
                  {selectedLog.tags.map(t => (
                    <span key={t} className="px-4 py-1.5 bg-black/5 opacity-50 rounded-xl text-[8px] font-black uppercase tracking-widest border border-black/5">#{t}</span>
                  ))}
                </div>
              </section>

              {/* Scope Guard Result */}
              <div className="pt-12 border-t border-black/5 space-y-10">
                <div className="flex items-center gap-4">
                  <div className={`${getActionBtnStyle()} p-3 rounded-xl shadow-lg`}>
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-7.618 3.016 11.955 11.955 0 01-4.616 11.037c1.64 3.107 4.909 5.31 8.614 6.006 3.705-.696 6.974-2.899 8.614-6.006a11.955 11.955 0 01-4.616-11.037z" /></svg>
                  </div>
                  <h4 className="text-[10px] font-black uppercase tracking-[0.4em] opacity-80">Profit Audit Output</h4>
                </div>

                {selectedLog.aiAnalysis ? (
                  <div className="space-y-12 animate-in slide-in-from-bottom-8 duration-700">
                    <div className="flex items-start gap-6">
                      <div className={`text-[10px] font-black px-4 py-2 rounded-full uppercase tracking-widest border shadow-sm shrink-0 mt-1 ${
                        selectedLog.aiAnalysis.scopeClassification === ScopeClassification.OUT_OF_SCOPE_LIKELY ? 'bg-rose-50 text-rose-600 border-rose-100' : 'bg-slate-50 text-slate-600 border-slate-100'
                      }`}>
                        {selectedLog.aiAnalysis.scopeClassification}
                      </div>
                      <p className="text-base opacity-50 font-light italic leading-relaxed">"{selectedLog.aiAnalysis.summary}"</p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
                      <div className="space-y-8">
                        <div className="space-y-4">
                          <p className="text-[9px] font-black opacity-30 uppercase tracking-[0.2em]">Forensic Recovery Logic</p>
                          <div className={`p-8 rounded-[2rem] border leading-relaxed text-sm ${selectedLog.aiAnalysis.scopeClassification === ScopeClassification.OUT_OF_SCOPE_LIKELY ? 'border-rose-100 bg-rose-50/10 text-rose-950' : 'border-black/5 bg-black/5 text-current'}`}>
                            {selectedLog.aiAnalysis.financialImpactRationale}
                          </div>
                        </div>

                        <div className="space-y-4">
                          <p className="text-[9px] font-black opacity-30 uppercase tracking-[0.2em]">Contract Alignment</p>
                          {selectedLog.aiAnalysis.wbsMatches.map(match => {
                            const wbsItem = wbs.find(w => w.id === match.id);
                            return (
                              <div key={match.id} className="p-6 rounded-[2rem] border border-black/5 bg-white shadow-sm group transition-all">
                                <div className="flex justify-between items-center mb-2">
                                  <span className="text-[10px] font-black">{wbsItem?.code}</span>
                                  <span className="text-[8px] font-black text-emerald-500 uppercase tracking-widest">{Math.round(match.confidence * 100)}% Match</span>
                                </div>
                                <p className="text-sm font-bold mb-2">{wbsItem?.title}</p>
                                <p className="text-[10px] opacity-40 leading-relaxed font-light italic line-clamp-2">"{match.rationale}"</p>
                              </div>
                            );
                          })}
                        </div>
                      </div>

                      <div className={`${getActionBtnStyle()} p-10 rounded-[2.5rem] flex flex-col justify-center text-center shadow-xl h-fit sticky top-10`}>
                        <p className="text-[8px] font-black opacity-40 uppercase tracking-[0.3em] mb-6">Action Hub</p>
                        <h5 className="text-3xl font-extralight tracking-tight mb-10 leading-tight">{selectedLog.aiAnalysis.recommendedAction}</h5>
                        {selectedLog.aiAnalysis.recommendedAction === 'DraftCO' && !selectedLog.linkedChangeOrderId && (
                          <button 
                            onClick={() => setIsCoModalOpen(true)}
                            className="w-full py-5 bg-white text-black rounded-2xl font-black text-[10px] uppercase tracking-[0.2em] hover:bg-slate-200 transition-all shadow-xl active:scale-95"
                          >
                            Generate CO
                          </button>
                        )}
                        <button className="w-full mt-6 py-4 text-[9px] font-black uppercase tracking-[0.2em] opacity-50 hover:opacity-100 transition-colors">
                          RFI Clarification
                        </button>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="p-16 border-2 border-dashed border-black/5 rounded-[2.5rem] text-center bg-black/5 opacity-40 group hover:opacity-100 transition-all cursor-pointer" onClick={handleAuditLogs}>
                    <p className="text-[9px] font-black tracking-[0.3em] uppercase opacity-40 mb-6 transition-colors">Forensic Engine Offline</p>
                    <button className="px-10 py-4 bg-white border border-black/10 text-black rounded-2xl text-[9px] font-black uppercase tracking-[0.1em] shadow-sm hover:shadow-lg transition-all">
                      Process Record
                    </button>
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className="h-full flex flex-col items-center justify-center opacity-10">
              <p className="text-[10px] font-black tracking-[0.5em] uppercase">Selection Pending</p>
            </div>
          )}
        </div>
      </main>

      {/* Modals remain essentially similar but should also respect the sizing constraints */}
      {isNewLogModalOpen && (
        <NewLogModal 
          isOpen={isNewLogModalOpen} 
          onClose={() => setIsNewLogModalOpen(false)} 
          onSave={(newLog) => {
            const entry: LogEntry = {
              ...newLog,
              id: `l${Date.now()}`,
              projectId: project.id,
              date: new Date().toISOString().split('T')[0],
              authorName: "Managing Principal",
              authorRole: "Principal",
              status: LogStatus.NEW,
              photos: []
            };
            setLogs([entry, ...logs]);
            setIsNewLogModalOpen(false);
          }}
        />
      )}

      {isCoModalOpen && selectedLog && (
        <CoDraftModal 
          isOpen={isCoModalOpen}
          onClose={() => setIsCoModalOpen(false)}
          sourceLog={selectedLog}
          analysis={selectedLog.aiAnalysis!}
          wbsContext={wbs.filter(w => selectedLog.aiAnalysis?.wbsMatches.some(m => m.id === w.id))}
          onSaveDraft={(co) => {
             setLogs(logs.map(l => l.id === selectedLog.id ? { ...l, linkedChangeOrderId: co.title, status: LogStatus.LINKED_TO_CO } : l));
             setIsCoModalOpen(false);
          }}
        />
      )}
    </div>
  );
};

export default LogsPage;
